import type { BinaryChannel, BinaryChannelObserver, BinaryChannelState } from './BinaryChannel';
import { ReconnectingWebSocketChannel, type ReconnectingWebSocketChannelOptions } from './ReconnectingWebSocketChannel';

export class NetworkHub {
  readonly #channels = new Set<ManagedBinaryChannel>();
  #disposed = false;

  openWebSocket(url: string, options?: ReconnectingWebSocketChannelOptions): BinaryChannel {
    if (this.#disposed) throw new Error('Network hub is disposed.');
    const inner = new ReconnectingWebSocketChannel(url, options);
    const channel = new ManagedBinaryChannel(inner, () => this.#channels.delete(channel));
    this.#channels.add(channel);
    return channel;
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    for (const channel of [...this.#channels]) channel.dispose();
    this.#channels.clear();
  }
}

class ManagedBinaryChannel implements BinaryChannel {
  readonly #inner: BinaryChannel;
  readonly #onDispose: () => void;
  #disposed = false;

  constructor(inner: BinaryChannel, onDispose: () => void) {
    this.#inner = inner;
    this.#onDispose = onDispose;
  }

  get state(): BinaryChannelState {
    return this.#inner.state;
  }

  connect(): Promise<void> {
    return this.#inner.connect();
  }

  send(payload: ArrayBuffer): boolean {
    return this.#inner.send(payload);
  }

  subscribe(observer: BinaryChannelObserver): () => void {
    return this.#inner.subscribe(observer);
  }

  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    this.#inner.dispose();
    this.#onDispose();
  }
}
